;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import type { PaginatedOffersResult } from '@qogita/canary-client/sellers'
import { Anchor } from '@qogita/ui/v2/anchor'
import { Icon } from '@qogita/ui/v2/icon'
import { UseQueryResult } from '@tanstack/react-query'
import React from 'react'
import { routes } from 'src/deprecated/utils/routes'

/*
 * Formats large numbers for display
 * 1,000,000 -> 1M
 */
const formatLargeNumberForDisplay = ({ count }: { count: number }) => {
  if (count > 999999) {
    return `${(count / 1000000).toFixed(1)}M`
  }
  return count.toLocaleString()
}

const WidgetContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex h-full min-h-[3rem] w-full flex-1 flex-col gap-4 sm:flex-row lg:gap-10">
      {children}
    </div>
  )
}

const WidgetItemContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex flex-1 flex-col items-start justify-between gap-2 p-6 max-md:min-w-[160px]">
      {children}
    </div>
  )
}

const LoadingBlock = () => {
  return (
    <div className="h-full min-w-[139px] animate-pulse rounded-md bg-gray-200" />
  )
}

function TotalProductsCard({
  queryResult,
}: {
  queryResult: UseQueryResult<PaginatedOffersResult, Error>
}) {
  if (queryResult.isPending) {
    return (
      <WidgetContainer>
        <WidgetItemContainer>
          <LoadingBlock />
        </WidgetItemContainer>
      </WidgetContainer>
    )
  }

  if (queryResult.error) {
    return null
  }

  return (
    <WidgetContainer>
      <WidgetItemContainer>
        <div className="flex min-w-40 flex-col gap-2 sm:min-w-90">
          <p className="typography-header-2xl-bold text-high-emphasis">
            {formatLargeNumberForDisplay({
              count: queryResult.data.count,
            })}{' '}
            <span className="typography-header-lg-semibold">products live</span>
          </p>
          <p className="typography-body-xs-normal text-low-emphasis text-nowrap">
            Excluding sold out products
          </p>
        </div>
      </WidgetItemContainer>
    </WidgetContainer>
  )
}

function HiddenProductsCard({ count }: { count: number }) {
  if (count) {
    return (
      <div className="border-default flex flex-col justify-center border-t p-6">
        <HiddenProducts count={count} />

        <div className="flex items-center gap-2">
          <Anchor href={routes.hiddenProducts} variant="text" size="small">
            Show details
          </Anchor>
          <span
            aria-hidden="true"
            className="typography-body-xs-normal text-low-emphasis"
          >
            ·
          </span>
          <span className="typography-body-xs-normal text-low-emphasis">
            Across all price lists
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className="border-default flex flex-col justify-center gap-2 border-t p-6">
      <EmptyState />

      <span className="typography-body-xs-normal text-low-emphasis">
        Across all price lists
      </span>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex gap-1">
      <Icon
        name="heroicons/outline/check-circle"
        // oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component
        className="text-semantic-success-bright size-6"
      />
      <p>No hidden products.</p>
    </div>
  )
}

function HiddenProducts({ count }: { count: number }) {
  return (
    <p className="typography-header-2xl-bold text-high-emphasis">
      {formatLargeNumberForDisplay({ count })}{' '}
      <span className="typography-header-lg-semibold">products hidden</span>
    </p>
  )
}

export const InventoryStatisticsCard = ({
  queryResult,
  hiddenProductsCount,
}: {
  queryResult: UseQueryResult<PaginatedOffersResult, Error>
  hiddenProductsCount: number
}) => {
  return (
    <div className="border-default bg-default flex h-full flex-col rounded-lg border shadow-xs">
      <TotalProductsCard queryResult={queryResult} />
      <HiddenProductsCard count={hiddenProductsCount} />
    </div>
  )
}
